import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { ButtonLink } from "./components/ButtonLink";
import { spreadDangerousChildren } from "../utils/spreadDangerousChildren";
import style from "./styles.pcss";
import { calculateTooltipPosition } from "./utils/calculateTooltipPosition";
var cn = classNames.bind(style);
export var Tooltip = /*#__PURE__*/memo(function (_ref) {
  var _cn, _cn3;
  var message = _ref.message,
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? 'bottom' : _ref$position,
    children = _ref.children,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'secondary' : _ref$variant,
    _ref$maxWidth = _ref.maxWidth,
    maxWidth = _ref$maxWidth === void 0 ? '225' : _ref$maxWidth,
    _ref$testName = _ref.testName,
    testName = _ref$testName === void 0 ? 'Tooltip' : _ref$testName,
    containerRef = _ref.containerRef,
    buttonConfig = _ref.buttonConfig,
    onOpen = _ref.onOpen,
    onClose = _ref.onClose;
  var tooltipRef = useRef(null);
  var wrapRef = useRef(null);
  var pointerRef = useRef(null);
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    right = _useState2[0],
    setRight = _useState2[1];
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    left = _useState4[0],
    setLeft = _useState4[1];
  var _useState5 = useState(undefined),
    _useState6 = _slicedToArray(_useState5, 2),
    shiftX = _useState6[0],
    setShiftX = _useState6[1];
  var _useState7 = useState(undefined),
    _useState8 = _slicedToArray(_useState7, 2),
    shiftY = _useState8[0],
    setShiftY = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isOpen = _useState10[0],
    setIsOpen = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isPositionCalculated = _useState12[0],
    setIsPositionCalculated = _useState12[1];
  var _useState13 = useState(position),
    _useState14 = _slicedToArray(_useState13, 2),
    calculatedPosition = _useState14[0],
    setCalculatedPosition = _useState14[1];
  useEffect(function () {
    setCalculatedPosition(position);
  }, [position]);
  useLayoutEffect(function () {
    if (isPositionCalculated) {
      return;
    }
    if (isOpen && tooltipRef.current && wrapRef.current && pointerRef.current) {
      var _containerRef$current;
      var containerWidth = window.innerWidth;
      var containerHeight = window.innerHeight;
      if (containerRef !== null && containerRef !== void 0 && containerRef.current) {
        containerWidth = containerRef.current.clientWidth;
        containerHeight = containerRef.current.clientHeight;
      }
      var calculatedPositionData = calculateTooltipPosition({
        tooltipMessageRect: tooltipRef.current.getBoundingClientRect(),
        tooltipWrapRect: wrapRef.current.getBoundingClientRect(),
        pointerWrapRect: pointerRef.current.getBoundingClientRect(),
        position: calculatedPosition,
        shiftX: shiftX,
        shiftY: shiftY,
        containerWidth: containerWidth,
        containerHeight: containerHeight,
        containerRect: containerRef === null || containerRef === void 0 ? void 0 : (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.getBoundingClientRect()
      });
      setRight(calculatedPositionData.right);
      setLeft(calculatedPositionData.left);
      setCalculatedPosition(calculatedPositionData.nextPosition);
      setShiftX(calculatedPositionData.shiftX);
      setShiftY(calculatedPositionData.shiftY);
      setIsPositionCalculated(true);
    }
  }, [isOpen, isPositionCalculated, calculatedPosition, shiftX, shiftY, tooltipRef, wrapRef, pointerRef, containerRef]);
  useEffect(function () {
    if (!isOpen) {
      setIsPositionCalculated(false);
      setRight(undefined);
      setLeft(undefined);
      setShiftX(undefined);
      setShiftY(undefined);
      setCalculatedPosition(position);
    }
  }, [isOpen, maxWidth, position]);
  var handleOpen = function handleOpen() {
    setIsOpen(true);
    onOpen === null || onOpen === void 0 ? void 0 : onOpen();
  };
  var handleClose = function handleClose() {
    setIsOpen(false);
    onClose === null || onClose === void 0 ? void 0 : onClose();
  };
  var messageTransform = useMemo(function () {
    var _ref2;
    return (_ref2 = shiftX !== null && shiftX !== void 0 ? shiftX : shiftY) !== null && _ref2 !== void 0 ? _ref2 : undefined;
  }, [shiftX, shiftY]);
  return /*#__PURE__*/React.createElement("div", {
    className: cn('tooltip', (_cn = {}, _defineProperty(_cn, "tooltip--".concat(calculatedPosition), !!calculatedPosition), _defineProperty(_cn, 'tooltip--open', isOpen), _cn)),
    "data-component": testName,
    onMouseEnter: handleOpen,
    onMouseLeave: handleClose,
    ref: wrapRef,
    "data-t-id": "src-Tooltip"
  }, isOpen && /*#__PURE__*/React.createElement("div", {
    className: cn('tooltip__bridge')
  }), /*#__PURE__*/React.createElement("button", {
    className: cn('tooltip__trigger', _defineProperty({}, "tooltip__trigger--".concat(calculatedPosition), !!calculatedPosition)),
    tabIndex: 0,
    type: "button"
  }, children), /*#__PURE__*/React.createElement("div", {
    className: cn('tooltip__message', (_cn3 = {}, _defineProperty(_cn3, "tooltip__message--".concat(variant), !!variant), _defineProperty(_cn3, "tooltip__message--".concat(calculatedPosition), !!calculatedPosition), _cn3)),
    ref: tooltipRef
    // eslint-disable-next-line react/forbid-dom-props
    ,
    style: {
      right: right,
      left: left,
      maxWidth: "".concat(maxWidth, "px"),
      transform: messageTransform
    }
  }, /*#__PURE__*/React.createElement("span", _extends({
    className: cn('tooltip__message-text')
  }, spreadDangerousChildren(message))), (buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.text) && /*#__PURE__*/React.createElement(ButtonLink, buttonConfig)), /*#__PURE__*/React.createElement("div", {
    className: cn('tooltip__pointer'),
    ref: pointerRef
  }));
});
Tooltip.displayName = Tooltip.name;